export interface XeroScopeDetail {
  description: string;
  required?: boolean;
}

export const XERO_SCOPES: Record<string, XeroScopeDetail> = {
  openid: {
    description:
      "Confirms who signed in. Required by Xero for OpenID Connect sign-in.",
  },
  profile: {
    description:
      "The name and Xero user id of the person who authorised the connection.",
  },
  email: {
    description:
      "The email address of the person who authorised the connection.",
  },
  offline_access: {
    description:
      "Lets Prism keep the connection alive on its own. Without it Xero issues no refresh token, so the connection stops working when the access token expires and an admin has to authorise again.",
    required: true,
  },

  // Accounting: what Prism reads.
  "accounting.journals.read": {
    description:
      "Read the general ledger journals. This is the data the Fabric pipeline loads into the lakehouse, so the journal ingestion cannot run without it.",
    required: true,
  },
  "accounting.reports.read": {
    description:
      "Read Xero's own reports, such as profit and loss and the balance sheet.",
  },
  "accounting.settings.read": {
    description:
      "Read the chart of accounts, tax rates and tracking categories.",
  },
  "accounting.settings": {
    description:
      "Read and change the chart of accounts, tax rates and tracking categories. Write access, broader than Prism needs.",
  },
  "accounting.transactions.read": {
    description:
      "Read invoices, bills, credit notes, payments and bank transactions.",
  },
  "accounting.transactions": {
    description:
      "Read and create invoices, bills, credit notes, payments and bank transactions. Write access, broader than Prism needs.",
  },
  "accounting.contacts.read": {
    description: "Read customer and supplier contact records.",
  },
  "accounting.contacts": {
    description:
      "Read and change customer and supplier contact records. Write access, broader than Prism needs.",
  },
  "accounting.attachments.read": {
    description: "Read files attached to transactions.",
  },
  "accounting.attachments": {
    description:
      "Read and add files attached to transactions. Write access, broader than Prism needs.",
  },
  "accounting.budgets.read": {
    description: "Read budgets set up in Xero.",
  },

  // Other Xero product areas, granted only if the consent asked for them.
  "assets.read": {
    description: "Read the fixed asset register.",
  },
  "files.read": {
    description: "Read the organisation's Xero file library.",
  },
  "projects.read": {
    description: "Read projects, tasks and time entries.",
  },
  "payroll.employees.read": {
    description: "Read employee records from Xero Payroll.",
  },
  "payroll.payruns.read": {
    description: "Read pay runs from Xero Payroll.",
  },
  "payroll.timesheets.read": {
    description: "Read timesheets from Xero Payroll.",
  },
  "payroll.settings.read": {
    description: "Read Xero Payroll settings, such as pay items and calendars.",
  },
};

function deriveDescription(scope: string): string {
  const readOnly = scope.endsWith(".read");
  const area = scope.split(".")[0].replaceAll("_", " ");
  const subject = scope
    .split(".")
    .slice(1)
    .filter((part) => part !== "read")
    .join(" ")
    .replaceAll("_", " ");

  const target = subject ? `${subject} in Xero ${area}` : `Xero ${area}`;
  return readOnly
    ? `Read-only access to ${target}.`
    : `Read and write access to ${target}.`;
}

export function describeScope(scope: string): XeroScopeDetail {
  return XERO_SCOPES[scope] ?? { description: deriveDescription(scope) };
}

export const REQUIRED_SCOPES: string[] = Object.entries(XERO_SCOPES)
  .filter(([, detail]) => detail.required)
  .map(([scope]) => scope);
